import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DonationCampaign, DonationRecord, Gelanggang, GelanggangDetail } from '../../types';
import {
  getDonationCampaignsDB,
  getDonationsDB,
  getGelanggangDB,
  saveDonationCampaignDB,
  deleteDonationCampaignDB,
  confirmDonationSuccessDB,
  deleteDonationDB,
  updateDonationRecordDB
} from '../../services/db';
import { OfficialDonationReceiptModal } from './OfficialDonationReceiptModal';
import { useToast } from '../common/Toast';
import { isSuperAdmin, hasRolePermission } from '../../utils/formatters';
import {
  Heart,
  TrendingUp,
  Users,
  Plus,
  Edit3,
  Trash2,
  CheckCircle2,
  Clock,
  Search,
  FileText,
  Eye,
  Sparkles,
  ExternalLink,
  Filter,
  Check,
  X,
  RefreshCw,
  Award
} from 'lucide-react';
import { formatCurrencyMYR } from '../../utils/formatters';

interface DashboardDonationsManagerProps {
  onNavigateToCampaign?: (campaignId: string) => void;
  onNavigateToCreate?: () => void;
  setActiveTab?: (tab: string) => void;
}

export const DashboardDonationsManager: React.FC<DashboardDonationsManagerProps> = ({
  onNavigateToCampaign,
  onNavigateToCreate,
  setActiveTab
}) => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const [campaigns, setCampaigns] = useState<DonationCampaign[]>([]);
  const [donations, setDonations] = useState<DonationRecord[]>([]);
  const [gelanggangList, setGelanggangList] = useState<GelanggangDetail[]>([]);
  const [activeSubTab, setActiveSubTab] = useState<'campaigns' | 'donations'>('campaigns');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('Semua');

  // Receipt Modal state
  const [selectedDonationForReceipt, setSelectedDonationForReceipt] = useState<DonationRecord | null>(null);
  const [isReceiptOpen, setIsReceiptOpen] = useState<boolean>(false);

  // Editing campaign inline / quick edit state
  const [editingCampaign, setEditingCampaign] = useState<DonationCampaign | null>(null);
  const [isQuickEditOpen, setIsQuickEditOpen] = useState<boolean>(false);
  const [editTitle, setEditTitle] = useState<string>('');
  const [editTargetAmount, setEditTargetAmount] = useState<number>(0);
  const [editBeneficiaryGelanggang, setEditBeneficiaryGelanggang] = useState<Gelanggang>('Semua Gelanggang');
  const [editStatus, setEditStatus] = useState<DonationCampaign['status']>('Aktif');
  const [editIsFeatured, setEditIsFeatured] = useState<boolean>(false);

  const isManagement = Boolean(
    currentUser &&
    hasRolePermission(currentUser, ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Bendahari', 'AJK'])
  );

  const loadData = async () => {
    try {
      const [campList, donList, gList] = await Promise.all([
        getDonationCampaignsDB(),
        getDonationsDB(),
        getGelanggangDB()
      ]);
      setCampaigns(campList);
      setDonations(donList);
      if (gList && gList.length > 0) {
        setGelanggangList(gList);
      }
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const handleUpdate = () => loadData();
    window.addEventListener('asmm_db_updated', handleUpdate);
    window.addEventListener('asmm_donation_received', handleUpdate);
    return () => {
      window.removeEventListener('asmm_db_updated', handleUpdate);
      window.removeEventListener('asmm_donation_received', handleUpdate);
    };
  }, []);

  const totalRaised = campaigns.reduce((sum, c) => sum + c.currentAmount, 0);
  const totalDonors = donations.filter(d => d.status === 'Berjaya').length;
  const pendingCount = donations.filter(d => d.status === 'Menunggu Pengesahan').length;

  const handleApproveDonation = async (donationId: string) => {
    try {
      const res = await confirmDonationSuccessDB(donationId);
      if (res.donation) {
        showToast('success', `Sumbangan ${formatCurrencyMYR(res.donation.amount)} telah disahkan!`);
        loadData();
      }
    } catch {
      showToast('error', 'Gagal mengesahkan sumbangan.');
    }
  };

  const handleDeleteDonation = async (donation: DonationRecord) => {
    if (window.confirm(`Adakah anda pasti untuk memadam rekod sumbangan ${formatCurrencyMYR(donation.amount)} daripada ${donation.donorName}?`)) {
      try {
        await deleteDonationDB(donation.id);
        showToast('success', 'Rekod sumbangan berjaya dipadam & baki kempen dikemaskini.');
        loadData();
      } catch {
        showToast('error', 'Gagal memadam rekod sumbangan.');
      }
    }
  };

  const handleDeleteCampaign = async (campaign: DonationCampaign) => {
    if (window.confirm(`Adakah anda pasti untuk memadam kempen "${campaign.title}"?`)) {
      try {
        await deleteDonationCampaignDB(campaign.id);
        showToast('success', 'Kempen infaq berjaya dipadam.');
        loadData();
      } catch {
        showToast('error', 'Gagal memadam kempen.');
      }
    }
  };

  const handleOpenQuickEdit = (camp: DonationCampaign) => {
    setEditingCampaign(camp);
    setEditTitle(camp.title);
    setEditTargetAmount(camp.targetAmount);
    setEditBeneficiaryGelanggang(camp.beneficiaryGelanggang || 'Semua Gelanggang');
    setEditStatus(camp.status);
    setEditIsFeatured(Boolean(camp.isFeatured));
    setIsQuickEditOpen(true);
  };

  const handleSaveQuickEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCampaign) return;

    try {
      const updated: DonationCampaign = {
        ...editingCampaign,
        title: editTitle.trim() || editingCampaign.title,
        targetAmount: Number(editTargetAmount) || editingCampaign.targetAmount,
        beneficiaryGelanggang: editBeneficiaryGelanggang,
        status: editStatus,
        isFeatured: editIsFeatured
      };

      await saveDonationCampaignDB(updated, editingCampaign.id);
      setIsQuickEditOpen(false);
      setEditingCampaign(null);
      showToast('success', 'Kempen berjaya dikemaskini secara langsung!');
      loadData();
    } catch {
      showToast('error', 'Gagal mengemaskini kempen.');
    }
  };

  // Filtered donations list
  const filteredDonations = donations.filter((d) => {
    const matchesStatus =
      selectedStatusFilter === 'Semua' || d.status === selectedStatusFilter;
    const matchesSearch =
      d.donorName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.transactionRef.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.campaignTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.donorEmail.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesStatus && matchesSearch;
  });

  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-5">
      {/* Top Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-100">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-1.5 bg-red-50 text-asmm-red rounded-xl">
              <Heart className="w-5 h-5 fill-asmm-red" />
            </span>
            <h3 className="text-base font-extrabold text-slate-900">
              Pengurusan Tabung Infaq & Crowdfunding (Live)
            </h3>
            {pendingCount > 0 && isManagement && (
              <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-bold rounded-full animate-pulse">
                {pendingCount} Menunggu Pengesahan
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Kutipan sumbangan telus, kemaskini kempen masa nyata, dan audit kewangan berpusat.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {isManagement && (
            <button
              type="button"
              onClick={() => {
                if (setActiveTab) setActiveTab('donations');
              }}
              className="px-3.5 py-2 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl shadow-xs flex items-center gap-1.5 cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Cipta Kempen Baru</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => {
              if (setActiveTab) setActiveTab('donations');
            }}
            className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl flex items-center gap-1.5 cursor-pointer"
          >
            <span>Laman Tabung Awam</span>
            <ExternalLink className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Live Mini Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-0.5">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Terkumpul</div>
          <div className="text-base sm:text-lg font-black text-emerald-700 font-mono">
            {formatCurrencyMYR(totalRaised)}
          </div>
        </div>

        <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-0.5">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Penderma</div>
          <div className="text-base sm:text-lg font-black text-blue-700 font-mono">
            {totalDonors} Orang
          </div>
        </div>

        <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-0.5">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Kempen Aktif</div>
          <div className="text-base sm:text-lg font-black text-slate-900 font-mono">
            {campaigns.filter(c => c.status === 'Aktif').length} Tabung
          </div>
        </div>

        <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-0.5">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Pengesahan</div>
          <div className={`text-base sm:text-lg font-black font-mono ${pendingCount > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
            {pendingCount} Tertunggak
          </div>
        </div>
      </div>

      {/* Sub-Tabs: Campaigns List vs Transactions List */}
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setActiveSubTab('campaigns')}
            className={`px-3.5 py-1.5 rounded-xl font-extrabold text-xs transition-all cursor-pointer ${
              activeSubTab === 'campaigns'
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Senarai Kempen Infaq ({campaigns.length})
          </button>

          <button
            type="button"
            onClick={() => setActiveSubTab('donations')}
            className={`px-3.5 py-1.5 rounded-xl font-extrabold text-xs transition-all cursor-pointer flex items-center gap-1.5 ${
              activeSubTab === 'donations'
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <span>Rekod Sumbangan ({donations.length})</span>
            {pendingCount > 0 && (
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-ping"></span>
            )}
          </button>
        </div>
      </div>

      {/* SUB-TAB 1: CAMPAIGNS CRUD TABLE */}
      {activeSubTab === 'campaigns' && (
        <div className="space-y-3">
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-200 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                  <th className="py-2.5 px-3">Kempen</th>
                  <th className="py-2.5 px-3">Kategori</th>
                  <th className="py-2.5 px-3">Sasaran & Kutipan</th>
                  <th className="py-2.5 px-3">Status</th>
                  {isManagement && <th className="py-2.5 px-3 text-right">Tindakan</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                {campaigns.map((camp) => {
                  const percent = Math.min(100, Math.round((camp.currentAmount / (camp.targetAmount || 1)) * 100));

                  return (
                    <tr key={camp.id} className="hover:bg-slate-50/70 transition-colors">
                      <td className="py-3 px-3">
                        <div className="font-extrabold text-slate-900 flex items-center gap-1.5">
                          <span>{camp.title}</span>
                          {camp.isFeatured && (
                            <span className="p-0.5 bg-amber-50 text-amber-700 rounded border border-amber-200" title="Pilihan Utama">
                              <Sparkles className="w-3 h-3" />
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-500">{camp.beneficiaryGelanggang === 'Semua Gelanggang' ? 'Semua Gelanggang' : (camp.beneficiaryGelanggang ? `Gelanggang ${camp.beneficiaryGelanggang}` : 'Semua Gelanggang')}</div>
                      </td>

                      <td className="py-3 px-3">
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-md font-bold text-[10px] border border-slate-200">
                          {camp.category}
                        </span>
                      </td>

                      <td className="py-3 px-3">
                        <div className="flex items-baseline gap-1 font-bold">
                          <span className="text-slate-900">{formatCurrencyMYR(camp.currentAmount)}</span>
                          <span className="text-slate-400 text-[10px]">/ {formatCurrencyMYR(camp.targetAmount)}</span>
                          <span className="text-asmm-red text-[10px] font-black ml-1">({percent}%)</span>
                        </div>
                        <div className="w-24 bg-slate-200 h-1.5 rounded-full overflow-hidden mt-1">
                          <div className="bg-asmm-red h-full rounded-full" style={{ width: `${percent}%` }} />
                        </div>
                      </td>

                      <td className="py-3 px-3">
                        <span
                          className={`px-2 py-0.5 rounded-full font-bold text-[10px] border ${
                            camp.status === 'Aktif'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : camp.status === 'Selesai'
                              ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : 'bg-slate-100 text-slate-600 border-slate-200'
                          }`}
                        >
                          {camp.status}
                        </span>
                      </td>

                      {isManagement && (
                        <td className="py-3 px-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleOpenQuickEdit(camp)}
                              className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors cursor-pointer"
                              title="Kemaskini Pantas"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDeleteCampaign(camp)}
                              className="p-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors cursor-pointer"
                              title="Padam Kempen"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SUB-TAB 2: DONATIONS RECORDS & APPROVALS CRUD */}
      {activeSubTab === 'donations' && (
        <div className="space-y-3">
          {/* Search & Status Filters */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cari penderma, no rujukan, atau kempen..."
                className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-hidden"
              />
            </div>

            <div className="flex items-center gap-1 overflow-x-auto text-[11px] font-bold">
              {['Semua', 'Berjaya', 'Menunggu Pengesahan'].map((st) => (
                <button
                  key={st}
                  type="button"
                  onClick={() => setSelectedStatusFilter(st)}
                  className={`px-3 py-1 rounded-xl transition-all cursor-pointer whitespace-nowrap ${
                    selectedStatusFilter === st
                      ? 'bg-asmm-red text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {st}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-200 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                  <th className="py-2.5 px-3">Penderma</th>
                  <th className="py-2.5 px-3">Kempen & Kaedah</th>
                  <th className="py-2.5 px-3">Jumlah</th>
                  <th className="py-2.5 px-3">Status</th>
                  <th className="py-2.5 px-3 text-right">Tindakan</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                {filteredDonations.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-slate-400 font-bold">
                      Tiada rekod sumbangan ditemui.
                    </td>
                  </tr>
                ) : (
                  filteredDonations.map((don) => {
                    const displayName = don.isAnonymous ? 'Hamba Allah' : don.donorName;

                    return (
                      <tr key={don.id} className="hover:bg-slate-50/70 transition-colors">
                        <td className="py-3 px-3">
                          <div className="font-extrabold text-slate-900">{displayName}</div>
                          <div className="text-[10px] text-slate-400 font-mono">{don.transactionRef}</div>
                          {don.message && (
                            <div className="text-[10px] text-slate-500 italic truncate max-w-xs mt-0.5">
                              "{don.message}"
                            </div>
                          )}
                        </td>

                        <td className="py-3 px-3">
                          <div className="font-bold text-slate-800 truncate max-w-xs">{don.campaignTitle}</div>
                          <div className="text-[10px] text-slate-500">{don.paymentMethod} • {don.createdAt}</div>
                        </td>

                        <td className="py-3 px-3">
                          <div className="font-black text-asmm-red text-xs">
                            {formatCurrencyMYR(don.amount)}
                          </div>
                        </td>

                        <td className="py-3 px-3">
                          <span
                            className={`px-2 py-0.5 rounded-full font-bold text-[10px] border flex items-center gap-1 w-fit ${
                              don.status === 'Berjaya'
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : don.status === 'Menunggu Pengesahan'
                                ? 'bg-amber-50 text-amber-800 border-amber-200'
                                : 'bg-red-50 text-red-700 border-red-200'
                            }`}
                          >
                            {don.status === 'Berjaya' && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
                            {don.status === 'Menunggu Pengesahan' && <Clock className="w-3 h-3 text-amber-600" />}
                            <span>{don.status}</span>
                          </span>
                        </td>

                        <td className="py-3 px-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {/* Approve button for pending manual transfers */}
                            {don.status === 'Menunggu Pengesahan' && isManagement && (
                              <button
                                type="button"
                                onClick={() => handleApproveDonation(don.id)}
                                className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[10px] rounded-lg shadow-xs flex items-center gap-1 cursor-pointer"
                                title="Sahkan Bayaran"
                              >
                                <Check className="w-3 h-3" />
                                <span>Sahkan</span>
                              </button>
                            )}

                            {/* View printable receipt */}
                            {don.status === 'Berjaya' && (
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedDonationForReceipt(don);
                                  setIsReceiptOpen(true);
                                }}
                                className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors cursor-pointer"
                                title="Lihat Resit"
                              >
                                <FileText className="w-3.5 h-3.5" />
                              </button>
                            )}

                            {/* Delete record */}
                            {isManagement && (
                              <button
                                type="button"
                                onClick={() => handleDeleteDonation(don)}
                                className="p-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors cursor-pointer"
                                title="Padam Rekod"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Quick Edit Campaign Modal */}
      {isQuickEditOpen && editingCampaign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xs">
          <form
            onSubmit={handleSaveQuickEdit}
            className="bg-white rounded-3xl p-6 border border-slate-200 shadow-xl max-w-md w-full space-y-4 text-xs"
          >
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <h4 className="font-extrabold text-sm text-slate-900">Kemaskini Tabung Infaq</h4>
              <button
                type="button"
                onClick={() => setIsQuickEditOpen(false)}
                className="p-1 text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-1">
              <label className="font-bold text-slate-700 block">Tajuk Kempen</label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl font-bold outline-hidden"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="font-bold text-slate-700 block">Sasaran (RM)</label>
                <input
                  type="number"
                  value={editTargetAmount}
                  onChange={(e) => setEditTargetAmount(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl font-bold outline-hidden"
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="font-bold text-slate-700 block">Status</label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as any)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl font-bold outline-hidden"
                >
                  <option value="Aktif">Aktif</option>
                  <option value="Draf">Draf</option>
                  <option value="Selesai">Selesai</option>
                </select>
              </div>
            </div>

            <div className="space-y-1">
              <label className="font-bold text-slate-700 block">Gelanggang Penerima</label>
              <select
                value={editBeneficiaryGelanggang}
                onChange={(e) => setEditBeneficiaryGelanggang(e.target.value as any)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl font-semibold outline-hidden"
              >
                <option value="Semua Gelanggang">Semua Gelanggang</option>
                {gelanggangList
                  .filter(g => g.name !== 'Semua Gelanggang' && g.name !== 'Tiada')
                  .map(g => (
                    <option key={g.id || g.name} value={g.name}>
                      {g.name}
                    </option>
                  ))}
              </select>
            </div>

            <label className="p-3 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between cursor-pointer">
              <span className="font-bold text-slate-800">Kempen Pilihan Utama (Featured)</span>
              <input
                type="checkbox"
                checked={editIsFeatured}
                onChange={(e) => setEditIsFeatured(e.target.checked)}
                className="w-4 h-4 text-asmm-red rounded"
              />
            </label>

            <div className="pt-2 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsQuickEditOpen(false)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl cursor-pointer"
              >
                Batal
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold rounded-xl shadow-xs cursor-pointer"
              >
                Simpan Kemaskini
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Official ROS Infaq Receipt Modal */}
      <OfficialDonationReceiptModal
        isOpen={isReceiptOpen}
        onClose={() => setIsReceiptOpen(false)}
        donation={selectedDonationForReceipt}
      />
    </div>
  );
};
